import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const TerritorySelection = ({ onSubmit, onPrevious, isLoading, agentData }) => {
  const [selectedTerritories, setSelectedTerritories] = useState(agentData?.territories || []);
  const [errors, setErrors] = useState({});

  const availableTerritories = [
    {
      id: 'north-america',
      name: 'North America',
      countries: ['United States', 'Canada', 'Mexico'],
      availability: 'available',
      commission: '3.5%',
      description: 'High-volume market with established customer base',
      monthlyTarget: '$50,000',
      competitionLevel: 'Medium'
    },
    {
      id: 'europe',
      name: 'Europe',
      countries: ['United Kingdom', 'Germany', 'France', 'Italy', 'Spain'],
      availability: 'available',
      commission: '4.0%',
      description: 'Premium market with strict regulatory compliance',
      monthlyTarget: '$75,000',
      competitionLevel: 'High'
    },
    {
      id: 'asia-pacific',
      name: 'Asia Pacific',
      countries: ['Australia', 'New Zealand', 'Singapore', 'Japan'],
      availability: 'limited',
      commission: '3.8%',
      description: 'Growing market with emerging opportunities',
      monthlyTarget: '$40,000',
      competitionLevel: 'Low'
    },
    {
      id: 'middle-east',
      name: 'Middle East',
      countries: ['UAE', 'Saudi Arabia', 'Qatar', 'Kuwait'],
      availability: 'available',
      commission: '4.2%',
      description: 'High-value transactions with premium commission rates',
      monthlyTarget: '$100,000',
      competitionLevel: 'Low'
    },
    {
      id: 'south-america',
      name: 'South America',
      countries: ['Brazil', 'Argentina', 'Colombia', 'Chile'],
      availability: 'limited',
      commission: '3.2%',
      description: 'Emerging market with significant growth potential',
      monthlyTarget: '$30,000',
      competitionLevel: 'Medium'
    },
    {
      id: 'africa',
      name: 'Africa',
      countries: ['Nigeria', 'Kenya', 'South Africa', 'Ghana'],
      availability: 'exclusive',
      commission: '4.5%',
      description: 'Exclusive opportunities in fast-growing remittance corridors',
      monthlyTarget: '$25,000',
      competitionLevel: 'Very Low'
    }
  ];

  const handleTerritoryToggle = (territoryId) => {
    const territory = availableTerritories?.find(t => t?.id === territoryId);
    
    if (territory?.availability === 'exclusive' && selectedTerritories?.length >= 1 && !selectedTerritories?.includes(territoryId)) {
      // For exclusive territories, check if user can handle the commitment
      setErrors({ exclusive: 'Exclusive territories require full commitment and cannot be combined with other regions.' });
      return;
    }

    if (selectedTerritories?.includes(territoryId)) {
      setSelectedTerritories(prev => prev?.filter(id => id !== territoryId));
    } else {
      if (territory?.availability === 'exclusive') {
        // Exclusive territory replaces all others
        setSelectedTerritories([territoryId]);
      } else {
        // Remove exclusive territories if selecting non-exclusive
        const filteredTerritories = selectedTerritories?.filter(id => {
          const existingTerritory = availableTerritories?.find(t => t?.id === id);
          return existingTerritory?.availability !== 'exclusive';
        });
        setSelectedTerritories([...filteredTerritories, territoryId]);
      }
    }

    // Clear errors when user makes a selection
    if (errors?.territories) {
      setErrors(prev => ({ ...prev, territories: '' }));
    }
    if (errors?.exclusive) {
      setErrors(prev => ({ ...prev, exclusive: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (selectedTerritories?.length === 0) {
      newErrors.territories = 'Please select at least one territory to operate in';
    }

    // Check if agent type supports multiple territories
    if (agentData?.agentType === 'individual' && selectedTerritories?.length > 2) {
      newErrors.territories = 'Individual agents can select maximum 2 territories';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      const territoryData = {
        territories: selectedTerritories,
        territoryDetails: selectedTerritories?.map(id => 
          availableTerritories?.find(t => t?.id === id)
        )
      };
      onSubmit(territoryData);
    }
  };

  const getAvailabilityBadge = (availability) => {
    const badges = {
      available: { text: 'Available', className: 'bg-success/10 text-success' },
      limited: { text: 'Limited Spots', className: 'bg-warning/10 text-warning' },
      exclusive: { text: 'Exclusive Opportunity', className: 'bg-primary/10 text-primary' }
    };
    return badges?.[availability] || badges?.available;
  };

  const getCompetitionColor = (level) => {
    const colors = {
      'Very Low': 'text-success',
      'Low': 'text-success',
      'Medium': 'text-warning',
      'High': 'text-error'
    };
    return colors?.[level] || 'text-muted-foreground';
  };

  const getTotalCommissionRange = () => {
    const selectedTerritoryDetails = selectedTerritories?.map(id => 
      availableTerritories?.find(t => t?.id === id)
    );

    if (selectedTerritoryDetails?.length === 0) return '0%';

    const commissions = selectedTerritoryDetails?.map(t => parseFloat(t?.commission?.replace('%', '')));
    const min = Math.min(...commissions);
    const max = Math.max(...commissions);

    return min === max ? `${min}%` : `${min}% - ${max}%`;
  };

  const getTotalMonthlyTarget = () => {
    const selectedTerritoryDetails = selectedTerritories?.map(id => 
      availableTerritories?.find(t => t?.id === id)
    );

    const total = selectedTerritoryDetails?.reduce((sum, t) => {
      const amount = parseInt(t?.monthlyTarget?.replace('$', '')?.replace(',', ''));
      return sum + amount;
    }, 0);

    return `$${total?.toLocaleString()}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center space-y-2 mb-6">
        <h2 className="text-2xl font-semibold text-foreground">Territory Selection</h2>
        <p className="text-muted-foreground">
          Choose the regions where you'll operate as an agent. Each territory has different requirements and commission rates.
        </p>
      </div>

      {/* Agent Type Restrictions */}
      {agentData?.agentType === 'individual' && (
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900">Individual Agent Limits</p>
              <p className="text-blue-700">As an individual agent, you can select up to 2 territories to ensure quality service delivery.</p>
            </div>
          </div>
        </div>
      )}

      {/* Territory Cards */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Globe" size={20} />
          <span>Available Territories</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {availableTerritories?.map(territory => {
            const isSelected = selectedTerritories?.includes(territory?.id);
            const badge = getAvailabilityBadge(territory?.availability);
            const isDisabled = territory?.availability === 'limited' && !isSelected && selectedTerritories?.length >= 3;

            return (
              <div
                key={territory?.id}
                className={`
                  relative border-2 rounded-lg p-6 cursor-pointer transition-all duration-200
                  ${isSelected 
                    ? 'border-primary bg-primary/5' 
                    : isDisabled
                    ? 'border-muted bg-muted/50 opacity-60 cursor-not-allowed' :'border-border bg-card hover:border-primary/50'
                  }
                `}
                onClick={() => !isDisabled && handleTerritoryToggle(territory?.id)}
              >
                {/* Selection Indicator */}
                <div className="absolute top-4 right-4">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => !isDisabled && handleTerritoryToggle(territory?.id)}
                    disabled={isDisabled}
                  />
                </div>

                {/* Territory Header */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-start justify-between">
                    <h4 className="text-lg font-semibold text-foreground pr-8">{territory?.name}</h4>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${badge?.className}`}>
                      {badge?.text}
                    </span>
                    <span className="text-sm font-medium text-primary">
                      {territory?.commission} commission
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground">{territory?.description}</p>
                </div>

                {/* Countries */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-muted-foreground mb-2">INCLUDED COUNTRIES</p>
                  <div className="flex flex-wrap gap-1">
                    {territory?.countries?.map(country => (
                      <span 
                        key={country}
                        className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded"
                      >
                        {country}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Territory Stats */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monthly Target:</span>
                    <span className="font-medium">{territory?.monthlyTarget}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Competition Level:</span>
                    <span className={`font-medium ${getCompetitionColor(territory?.competitionLevel)}`}>
                      {territory?.competitionLevel}
                    </span>
                  </div>
                </div>

                {/* Exclusive Territory Badge */}
                {territory?.availability === 'exclusive' && (
                  <div className="mt-3 p-2 bg-gradient-to-r from-primary/10 to-primary/5 rounded border border-primary/20">
                    <div className="flex items-center space-x-2 text-sm">
                      <Icon name="Star" size={16} className="text-primary" />
                      <span className="font-medium text-primary">Exclusive Territory</span>
                    </div>
                    <p className="text-xs text-primary/70 mt-1">
                      Full territorial rights with premium commission rates
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {errors?.territories && (
          <p className="text-sm text-error">{errors?.territories}</p>
        )}

        {errors?.exclusive && (
          <p className="text-sm text-error">{errors?.exclusive}</p>
        )}
      </div>

      {/* Selection Summary */}
      {selectedTerritories?.length > 0 && (
        <div className="bg-muted/30 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="BarChart3" size={20} />
            <span>Territory Selection Summary</span>
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Selected Territories:</span>
              <p className="font-medium text-foreground">{selectedTerritories?.length} regions</p>
            </div>
            <div>
              <span className="text-muted-foreground">Commission Range:</span>
              <p className="font-medium text-foreground">{getTotalCommissionRange()}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Combined Monthly Target:</span>
              <p className="font-medium text-foreground">{getTotalMonthlyTarget()}</p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-success/10 rounded border border-success/20">
            <div className="flex items-start space-x-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-success">Territory Approval</p>
                <p className="text-success/70">
                  Your territory selection will be reviewed during the application process. 
                  Final approval depends on your business profile and market availability.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 pt-6">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconPosition="left"
          className="sm:w-auto"
        >
          Back to Verification
        </Button>
        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isLoading}
          iconName="ArrowRight"
          iconPosition="right"
          className="sm:flex-1"
          disabled={selectedTerritories?.length === 0}
        >
          Continue to Commission Preview
        </Button>
      </div>
    </form>
  );
};

export default TerritorySelection;